import type { 
  IsencaoIRResultType, 
  INSSResultType, 
  IRResultType, 
  FaixaBeneficio,
  TabelaImpostoItem 
} from "../calculadora-isencao-ir";
import {
  TABELA_INSS_2025,
  TABELA_IRRF_2025_VIGENTE,
  TABELA_IRRF_2026_PROPOSTA,
  CONSTANTES_ISENCAO_IR_2026,
  CONSTANTES_GERAIS,
  ValidationMessages,
} from "../calculadora-isencao-ir";

// ============================================
// UTILITÁRIOS
// ============================================

const arredondarMoeda = (valor: number): number => 
  Math.round(valor * CONSTANTES_GERAIS.CALCULO.DECIMAL_PRECISION) / CONSTANTES_GERAIS.CALCULO.DECIMAL_PRECISION;

const encontrarFaixaImposto = (valor: number, tabela: readonly TabelaImpostoItem[]): TabelaImpostoItem => {
  const faixa = tabela.find(item => valor <= item.limite);
  return faixa || tabela[tabela.length - 1];
};

const calcularPorcentagem = (valor: number, base: number): number => {
  return base > CONSTANTES_GERAIS.CALCULO.MIN_VALUE 
    ? (valor / base) * CONSTANTES_GERAIS.CALCULO.DECIMAL_PRECISION 
    : CONSTANTES_GERAIS.CALCULO.MIN_VALUE;
};

// ============================================
// VALIDAÇÕES
// ============================================

export const validarSalario = (salario: number): void => {
  if (salario <= CONSTANTES_GERAIS.CALCULO.MIN_VALUE) {
    throw new Error(ValidationMessages.SALARIO_INVALIDO);
  }
  
  if (salario > CONSTANTES_GERAIS.VALIDACAO.SALARIO_MAXIMO_SUGERIDO) {
    throw new Error(ValidationMessages.SALARIO_MUITO_ALTO);
  }
};

// ============================================
// CÁLCULO DE INSS
// ============================================

export const calcularINSS = (salario: number): INSSResultType => {
  if (salario <= CONSTANTES_GERAIS.CALCULO.MIN_VALUE) {
    return { valor: 0, baseCalculo: 0, aliquotaEfetiva: 0 };
  }

  const baseCalculo = Math.min(salario, CONSTANTES_GERAIS.INSS.TETO_CONTRIBUICAO);
  const faixa = encontrarFaixaImposto(baseCalculo, TABELA_INSS_2025);
  
  const valorINSS = Math.max(
    CONSTANTES_GERAIS.CALCULO.MIN_VALUE, 
    baseCalculo * faixa.aliquota - faixa.parcelaDeducao
  );
  
  const aliquotaEfetiva = calcularPorcentagem(valorINSS, baseCalculo);

  return {
    valor: arredondarMoeda(valorINSS),
    baseCalculo: arredondarMoeda(baseCalculo),
    aliquotaEfetiva: arredondarMoeda(aliquotaEfetiva),
  };
};

// ============================================
// CÁLCULO DE IR ATUAL (2025)
// ============================================

export const calcularIRAtual = (salario: number, inss: number): IRResultType => {
  const baseCalculo = Math.max(CONSTANTES_GERAIS.CALCULO.MIN_VALUE, salario - inss);

  if (baseCalculo <= CONSTANTES_GERAIS.CALCULO.MIN_VALUE) {
    return { 
      valor: 0, 
      baseCalculo: arredondarMoeda(baseCalculo),
      aliquota: 0
    };
  }

  const faixa = encontrarFaixaImposto(baseCalculo, TABELA_IRRF_2025_VIGENTE);
  const valorIR = Math.max(
    CONSTANTES_GERAIS.CALCULO.MIN_VALUE, 
    baseCalculo * faixa.aliquota - faixa.parcelaDeducao
  );

  const aliquotaEfetiva = valorIR > 0 
    ? calcularPorcentagem(valorIR, baseCalculo)
    : 0;

  return {
    valor: arredondarMoeda(valorIR),
    baseCalculo: arredondarMoeda(baseCalculo),
    aliquota: arredondarMoeda(aliquotaEfetiva),
  };
};

// ============================================
// CÁLCULO DA REDUÇÃO DO IR (PL 1.087/2025)
// ============================================

export const calcularReducaoIR = (baseCalculo: number, irCalculado: number): number => {
  // A redução do PL 1.087/2025 aplica-se a TODAS as bases até R$ 7.350
  // Não há limite inferior - qualquer pessoa que pague IR pode se beneficiar
  
  // Cenário 1: Base até R$ 5.000 - REDUÇÃO MÁXIMA FIXA
  // Art. 3º-A: "Até R$ 5.000,00: Até 312,89 (de modo que o imposto devido seja zero)"
  if (baseCalculo <= CONSTANTES_ISENCAO_IR_2026.REDUCAO_MAXIMA.LIMITE_BASE) {
    // Redução de ATÉ R$ 312,89, limitada ao IR calculado
    return Math.min(CONSTANTES_ISENCAO_IR_2026.REDUCAO_MAXIMA.VALOR_REDUCAO, irCalculado);
  }

  // Cenário 2: Base entre R$ 5.000,01 e R$ 7.350 - REDUÇÃO DECRESCENTE LINEAR
  // Substitutivo Arthur Lira: "978,61 - (0,133145 x rendimentos tributáveis)"
  if (baseCalculo <= CONSTANTES_ISENCAO_IR_2026.REDUCAO_PROGRESSIVA.LIMITE_BASE_SUPERIOR) {
    const reducaoCalculada = 
      CONSTANTES_ISENCAO_IR_2026.REDUCAO_PROGRESSIVA.CONSTANTE_FIXA - 
      (CONSTANTES_ISENCAO_IR_2026.REDUCAO_PROGRESSIVA.COEFICIENTE * baseCalculo);
    
    // A redução não pode ser negativa nem maior que o IR calculado
    return Math.max(0, Math.min(reducaoCalculada, irCalculado));
  }

  // Cenário 3: Base acima de R$ 7.350 - SEM REDUÇÃO
  // Art. 3º-A, § 2º: "Os contribuintes que tiverem rendimentos tributáveis 
  // superiores a R$ 7.350,00 não terão redução"
  return 0;
};

// ============================================
// CÁLCULO DE IR COM NOVA TABELA 2026 (SEM REDUÇÃO PL)
// ============================================

const calcularIRComNovaTabela = (baseCalculo: number): number => {
  if (baseCalculo <= CONSTANTES_GERAIS.CALCULO.MIN_VALUE) {
    return 0;
  }

  const faixa = encontrarFaixaImposto(baseCalculo, TABELA_IRRF_2026_PROPOSTA);
  const valorIR = Math.max(
    CONSTANTES_GERAIS.CALCULO.MIN_VALUE, 
    baseCalculo * faixa.aliquota - faixa.parcelaDeducao
  );

  return arredondarMoeda(valorIR);
};

// ============================================
// CÁLCULO DE IR PROPOSTO (2026 - PL 1.087/2025)
// ============================================

export const calcularIRProposto = (salario: number, inss: number): IRResultType => {
  const baseCalculo = Math.max(CONSTANTES_GERAIS.CALCULO.MIN_VALUE, salario - inss);

  if (baseCalculo <= CONSTANTES_GERAIS.CALCULO.MIN_VALUE) {
    return { 
      valor: 0, 
      baseCalculo: arredondarMoeda(baseCalculo),
      aliquota: 0
    };
  }

  // Calcular IR com a NOVA tabela progressiva de 2026
  const irCalculado = calcularIRComNovaTabela(baseCalculo);

  // Calcular a redução aplicável conforme PL 1.087/2025
  const reducao = calcularReducaoIR(baseCalculo, irCalculado);

  // IR proposto = IR calculado - redução
  const valorIRFinal = Math.max(0, irCalculado - reducao);

  const aliquotaEfetiva = valorIRFinal > 0 && baseCalculo > 0
    ? calcularPorcentagem(valorIRFinal, baseCalculo)
    : 0;

  return {
    valor: arredondarMoeda(valorIRFinal),
    baseCalculo: arredondarMoeda(baseCalculo),
    aliquota: arredondarMoeda(aliquotaEfetiva),
  };
};

// ============================================
// DETERMINAR FAIXA DE BENEFÍCIO
// ============================================

export const determinarFaixaBeneficio = (
  baseCalculo: number,
  irAtual: number, 
  reducaoAplicadaPL: number
): FaixaBeneficio => {
  // Já está isento (não paga IR atualmente)
  if (irAtual === 0) {
    return 'JA_ISENTO';
  }

  // Se houve redução do PL aplicada
  if (reducaoAplicadaPL > 0) {
    // Base até R$ 5.000 - redução máxima fixa
    if (baseCalculo <= CONSTANTES_ISENCAO_IR_2026.REDUCAO_MAXIMA.LIMITE_BASE) {
      return 'BENEFICIADO_TOTAL';
    }

    // Base entre R$ 5.000,01 e R$ 7.350 - redução decrescente
    if (baseCalculo <= CONSTANTES_ISENCAO_IR_2026.REDUCAO_PROGRESSIVA.LIMITE_BASE_SUPERIOR) {
      return 'BENEFICIADO_PARCIAL';
    }
  }

  // Base acima de R$ 7.350 ou não houve redução aplicada
  return 'NAO_BENEFICIADO';
};

// ============================================
// CÁLCULO COMPLETO
// ============================================

export const calcularIsencaoIR = (salarioBruto: number): IsencaoIRResultType => {
  // Validação
  validarSalario(salarioBruto);

  // Cálculo do INSS
  const inssResult = calcularINSS(salarioBruto);
  const baseCalculoIR = salarioBruto - inssResult.valor;

  // Cálculo do IR atual (2025)
  const irAtualResult = calcularIRAtual(salarioBruto, inssResult.valor);

  // Cálculo do IR com nova tabela 2026
  const irNovaTabela = calcularIRComNovaTabela(baseCalculoIR);

  // Calcular a redução ESPECÍFICA do PL 1.087/2025
  const reducaoPL = calcularReducaoIR(baseCalculoIR, irNovaTabela);

  // Cálculo do IR proposto (2026)
  const irPropostoResult = calcularIRProposto(salarioBruto, inssResult.valor);

  // Determinar faixa de benefício (baseado na redução do PL)
  const faixaBeneficio = determinarFaixaBeneficio(
    baseCalculoIR,
    irAtualResult.valor,
    reducaoPL
  );

  // Economia TOTAL = IR atual - IR proposto
  const economiaTotal = irAtualResult.valor - irPropostoResult.valor;

  // ECONOMIA A EXIBIR: Apenas se houver economia real e não for JA_ISENTO
  const economiaMensal = faixaBeneficio !== 'JA_ISENTO' && economiaTotal > 0
    ? economiaTotal
    : 0;

  const economiaAnual = economiaMensal * CONSTANTES_GERAIS.CALCULO.PARCELAS_ANUAIS;

  const percentualEconomia = economiaMensal > 0 && irAtualResult.valor > 0
    ? calcularPorcentagem(economiaMensal, irAtualResult.valor)
    : 0;

  return {
    salarioBruto: arredondarMoeda(salarioBruto),
    inssDescontado: inssResult.valor,
    baseCalculoIR: arredondarMoeda(baseCalculoIR),
    irAtual: irAtualResult.valor,
    irProposto: irPropostoResult.valor,
    reducaoAplicada: arredondarMoeda(reducaoPL),
    economiaMensal: arredondarMoeda(economiaMensal),
    economiaAnual: arredondarMoeda(economiaAnual),
    percentualEconomia: arredondarMoeda(percentualEconomia),
    faixaBeneficio,
    aliquotaAtual: irAtualResult.aliquota,
    aliquotaProposta: irPropostoResult.aliquota,
  };
};

export const deveExibirDetalhamento = (resultado: IsencaoIRResultType): boolean => {
  return resultado.faixaBeneficio !== 'JA_ISENTO' &&
         resultado.economiaMensal > 0;
};